import { useState, useEffect } from 'react';
import { Horizon, Btn, Mo, Bd, Dp, Label } from './shared';
import { GOLD, EMBER, MONO } from '../lib/constants';
import { CATEGORY_LABELS, CAT_ICONS, Card, BackBtn } from './InsightsShared';

const API = process.env.REACT_APP_BACKEND_URL;

/* ══════════════════════════════════════
   ACCOUNT SELECT
   ══════════════════════════════════════ */
export function AccountSelect({ accounts, onSelect, institution, onBack }) {
  const sorted = [...accounts].sort((a, b) => {
    const rank = (acc) => {
      const sub = (acc.subtype || '').toLowerCase();
      const typ = (acc.type || '').toLowerCase();
      if (sub === 'checking' || typ === 'depository') return 0;
      if (sub === 'savings') return 1;
      return 2;
    };
    return rank(a) - rank(b);
  });

  const checkingAccounts = sorted.filter(a => (a.subtype || '').toLowerCase() === 'checking');
  const hasOnlyOneChecking = checkingAccounts.length === 1;
  const isChecking = (a) => (a.subtype || '').toLowerCase() === 'checking';
  const isCredit = (a) => (a.subtype || '').toLowerCase() === 'credit card' || (a.type || '').toLowerCase() === 'credit';

  return (
    <div className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div style={{ height: 'env(safe-area-inset-top, 20px)', minHeight: 20 }} />
        {onBack && <BackBtn onClick={onBack} />}
        <Label>DEEP SPENDING ANALYSIS</Label>
        <Bd style={{ fontSize: 14, color: 'var(--light)', display: 'block', marginBottom: 8, lineHeight: 1.6, fontWeight: 600 }}>
          Pick your main checking account.
        </Bd>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24, lineHeight: 1.6 }}>
          Choose the account where your paycheck lands and your daily spending happens. That's where the real story is.
        </Bd>
        {institution && <Mo style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>{institution.toUpperCase()}</Mo>}
        {sorted.map(a => {
          const checking = isChecking(a);
          const credit = isCredit(a);
          const recommended = checking && (hasOnlyOneChecking || sorted.indexOf(a) === 0);
          return (
            <button key={a.id} onClick={() => onSelect(a)} data-testid={`account-${a.id}`} style={{
              width: '100%', padding: '16px', marginBottom: 8,
              background: recommended ? `${GOLD}08` : 'var(--surface)',
              border: recommended ? `1px solid ${GOLD}40` : '1px solid var(--faint)',
              borderRadius: 12, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left',
              opacity: credit ? 0.6 : 1,
            }}>
              <div>
                <div className="dettio-flex dettio-flex--center dettio-flex--gap-sm">
                  <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{a.name}</Bd>
                  {recommended && (
                    <Mo data-testid="recommended-badge" style={{ fontSize: 8, fontWeight: 700, color: GOLD, background: `${GOLD}20`, padding: '2px 6px', borderRadius: 4 }}>RECOMMENDED</Mo>
                  )}
                  {credit && (
                    <Mo style={{ fontSize: 8, fontWeight: 700, color: 'var(--mute)', background: 'var(--faint)', padding: '2px 6px', borderRadius: 4 }}>CREDIT CARD</Mo>
                  )}
                </div>
                <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 4 }}>
                  {a.subtype || a.type}{a.mask ? ` ···${a.mask}` : ''}
                </Mo>
                {recommended && (
                  <Mo style={{ fontSize: 10, color: 'var(--gold)', display: 'block', marginTop: 4 }}>
                    This looks like your main account
                  </Mo>
                )}
              </div>
              <Mo style={{ fontSize: 14, fontWeight: 700, color: 'var(--light)' }}>${a.balance?.toLocaleString() || '\u2014'}</Mo>
            </button>
          );
        })}
        <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--faint)', borderRadius: 8 }}>
          <Bd style={{ fontSize: 11, color: 'var(--mute)', display: 'block', lineHeight: 1.5 }}>
            <span style={{ fontWeight: 700 }}>Tip:</span> For the best analysis, select a checking or debit account where your income arrives.
          </Bd>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   TIMEFRAME SELECT
   ══════════════════════════════════════ */
export function TimeframeSelect({ account, onSelect, onBack }) {
  return (
    <div className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div style={{ height: 'env(safe-area-inset-top, 20px)', minHeight: 20 }} />
        <BackBtn onClick={onBack} />
        <Label>HOW FAR BACK?</Label>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', display: 'block', marginBottom: 8, lineHeight: 1.6 }}>
          Analyzing: <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{account.name}</span>
        </Bd>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24, lineHeight: 1.6 }}>
          More months = better patterns. We recommend 6 months for the most accurate analysis.
        </Bd>
        {[{ months: 3, label: '3 Months', desc: 'Recent snapshot' }, { months: 6, label: '6 Months', desc: 'Recommended \u2014 best pattern detection' }].map(o => (
          <button key={o.months} onClick={() => onSelect(o.months)} data-testid={`timeframe-${o.months}`} style={{
            width: '100%', padding: '20px', marginBottom: 12, background: o.months === 6 ? `${GOLD}08` : 'var(--surface)',
            border: o.months === 6 ? `1px solid ${GOLD}40` : '1px solid var(--faint)', borderRadius: 12,
            cursor: 'pointer', textAlign: 'left',
          }}>
            <Dp style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)', display: 'block' }}>{o.label}</Dp>
            <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 4 }}>{o.desc}</Mo>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   LOADING SCREEN
   ══════════════════════════════════════ */
export function LoadingScreen({ stats }) {
  const [step, setStep] = useState(0);
  const steps = ['Pulling your transactions...', 'Grouping by category...', 'Detecting income patterns...', 'Calculating spending ratios...', 'Running AI analysis...'];
  useEffect(() => {
    const iv = setInterval(() => setStep(s => s < steps.length - 1 ? s + 1 : s), 1800);
    return () => clearInterval(iv);
  }, [steps.length]);

  return (
    <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', position: 'relative' }}>
      <Horizon intensity={0.5} />
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', width: '100%' }}>
        <div style={{ width: 64, height: 64, margin: '0 auto 24px' }}>
          <svg width={64} height={64} viewBox="0 0 64 64" style={{ animation: 'spin 2s linear infinite' }}>
            <circle cx="32" cy="32" r="28" fill="none" stroke="var(--faint)" strokeWidth="3" />
            <circle cx="32" cy="32" r="28" fill="none" stroke={GOLD} strokeWidth="3" strokeDasharray="60 120" strokeLinecap="round" />
          </svg>
        </div>
        <Dp style={{ fontSize: 18, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 12 }}>{steps[step]}</Dp>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 20, marginBottom: 32 }}>
          {steps.map((_, i) => (
            <div key={i} style={{ width: i <= step ? 24 : 8, height: 4, borderRadius: 2, background: i <= step ? GOLD : 'var(--ghost)', transition: 'all 0.5s' }} />
          ))}
        </div>
        {stats && (
          <div className="fade-in dettio-flex" style={{ gap: 20, justifyContent: 'center', marginTop: 20 }}>
            <div style={{ textAlign: 'center' }}>
              <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--gold)', display: 'block' }}>{stats.transaction_count || '\u2014'}</Dp>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1.5, display: 'block' }}>TRANSACTIONS</Mo>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--gold)', display: 'block' }}>{stats.categories?.length || '\u2014'}</Dp>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1.5, display: 'block' }}>CATEGORIES</Mo>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   NO BANK
   ══════════════════════════════════════ */
export function NoBank({ navigate }) {
  return (
    <div className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner" style={{ textAlign: 'center', paddingTop: 60 }}>
        <svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="var(--mute)" strokeWidth="1.5" style={{ marginBottom: 16 }}>
          <path d="M3 21h18M3 10h18M12 3l9 7H3l9-7z" /><path d="M5 10v11M9 10v11M15 10v11M19 10v11" />
        </svg>
        <Dp style={{ fontSize: 18, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 8 }}>Connect Your Bank</Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', lineHeight: 1.6, marginBottom: 24 }}>
          To give you real spending insights, connect the bank account where your paycheck lands and your daily spending happens.
        </Bd>
        <Btn onClick={() => navigate('bank-manage')} gold>CONNECT BANK</Btn>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   BUDGET TARGETS SCREEN
   ══════════════════════════════════════ */
export function BudgetTargetsScreen({ categories, targets, onSave, onBack }) {
  const [localTargets, setLocalTargets] = useState(() => {
    const existing = {};
    (targets || []).forEach(t => { existing[t.category] = t.target; });
    return existing;
  });
  const [saving, setSaving] = useState(false);

  const discretionary = (categories || []).filter(c => c.classification === 'discretionary');

  const handleSave = async () => {
    setSaving(true);
    const t = Object.entries(localTargets).filter(([, v]) => v > 0).map(([cat, target]) => {
      const c = categories.find(x => x.name === cat);
      return {
        category: cat,
        display_name: c?.display_name || CATEGORY_LABELS[cat] || cat,
        target: Math.round(target),
        current_spent: c?.monthly_avg || 0,
      };
    });
    try {
      await fetch(`${API}/api/insights/budget-targets`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targets: t }),
      });
    } catch {}
    setSaving(false);
    onSave(t);
  };

  return (
    <div className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div style={{ height: 'env(safe-area-inset-top, 20px)', minHeight: 20 }} />
        <BackBtn onClick={onBack} />
        <Label>SET BUDGET TARGETS</Label>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 20, lineHeight: 1.6 }}>
          Set monthly spending limits for each category. We'll track your progress.
        </Bd>

        {discretionary.length > 0 ? discretionary.map(cat => {
          const current = localTargets[cat.name] || 0;
          return (
            <div key={cat.name} data-testid={`budget-target-${cat.name}`} style={{ marginBottom: 16, padding: '14px', background: 'var(--faint)', borderRadius: 10 }}>
              <div className="dettio-flex dettio-flex--between" style={{ marginBottom: 6 }}>
                <Bd style={{ fontSize: 13, color: 'var(--light)', fontWeight: 600 }}>
                  {CAT_ICONS[cat.name] || ''} {cat.display_name}
                </Bd>
                <Mo style={{ fontSize: 11, color: 'var(--mute)' }}>Currently ${cat.monthly_avg?.toLocaleString()}/mo</Mo>
              </div>
              <div className="dettio-flex dettio-flex--center dettio-flex--gap-sm">
                <Mo style={{ fontSize: 12, color: 'var(--mute)' }}>$</Mo>
                <input
                  data-testid={`budget-input-${cat.name}`}
                  type="number"
                  placeholder={Math.round(cat.monthly_avg * 0.8).toString()}
                  value={current || ''}
                  onChange={(e) => setLocalTargets(p => ({ ...p, [cat.name]: parseFloat(e.target.value) || 0 }))}
                  style={{
                    flex: 1, padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--faint)',
                    borderRadius: 8, color: 'var(--light)', fontFamily: MONO, fontSize: 14, outline: 'none',
                  }}
                />
                <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>/mo</Mo>
              </div>
            </div>
          );
        }) : (
          <Card style={{ textAlign: 'center', padding: 20 }}>
            <Bd style={{ fontSize: 13, color: 'var(--mute)' }}>Run a spending analysis first to set budget targets.</Bd>
          </Card>
        )}

        {discretionary.length > 0 && (
          <Btn data-testid="save-budgets-btn" onClick={handleSave} gold disabled={saving} style={{ marginTop: 8 }}>
            {saving ? 'SAVING...' : 'SAVE TARGETS'}
          </Btn>
        )}
      </div>
    </div>
  );
}
